/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRouter } from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';
import { ArrowPreviousIcon } from '@coursera/cds-icons';

import LoadingSpinner from 'bundles/grading/components/common/LoadingSpinner';
import type { GetUrlFn } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import type {
  LinearNavigationButtonAction,
  LinearNavigationButtonInfo,
  NavigationShortcutCallbacks,
  SGASubmitter,
} from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { useEventTracker } from 'bundles/grading/utils/hooks';

import _t from 'i18n!nls/grading';

type Props = {
  previousButtonInfo: LinearNavigationButtonInfo;
  disabled: boolean;
  currentSubmitterId: string;
  previousSubmitter: SGASubmitter | null;
  getUrlCallback: GetUrlFn;
  shortcutCallbacks: NavigationShortcutCallbacks;
};

const styles = {
  container: () => css`
    height: 100%;
  `,
};

// "Previous" button for SGA grade by submitter flow
const PreviousButtonSubmitterFlow: React.FC<Props> = ({
  previousButtonInfo,
  disabled,
  currentSubmitterId,
  previousSubmitter,
  getUrlCallback,
  shortcutCallbacks,
}) => {
  const router = useRouter();
  const trackPreviousButtonOnClickUsage = useEventTracker();

  // default previous button action
  const previousButtonAction: LinearNavigationButtonAction = {
    title: _t('Previous'),
  };

  // if the previousButtonInfo is not null that means we're still in the current submission so use the details from the submissionManager
  if (previousButtonInfo !== null) {
    previousButtonAction.buttonCallback = () =>
      router.push(getUrlCallback(currentSubmitterId, previousButtonInfo?.questionId, previousButtonInfo?.rubricId));
  }
  // if there is a valid previous submitter, then show the previous submitter for previous button
  // add the 'last-part' flag so the SubmissionManager sets the last question/rubric for previous submitter
  else if (previousSubmitter !== null) {
    previousButtonAction.buttonCallback = () =>
      router.push(getUrlCallback(previousSubmitter?.id, undefined, undefined, true));
  }

  // eslint-disable-next-line no-param-reassign
  shortcutCallbacks.previousButtonCallback = disabled ? () => undefined : previousButtonAction.buttonCallback;

  return (
    <Button
      icon={disabled ? <LoadingSpinner /> : <ArrowPreviousIcon />}
      iconPosition="before"
      variant="secondary"
      css={styles.container}
      fullWidth
      onClick={() => {
        if (previousButtonAction?.buttonCallback) {
          previousButtonAction.buttonCallback();
          trackPreviousButtonOnClickUsage({
            trackingName: 'quick_grader_linear_nav_previous',
            action: 'click',
            trackingData: {},
          });
        }
      }}
      disabled={disabled || !previousButtonAction.buttonCallback} // disabled for first submitter / first question/rubric
    >
      {previousButtonAction.title}
    </Button>
  );
};

export default PreviousButtonSubmitterFlow;
